/* Koziva - small behaviours the theme settings cannot express.
 *
 * Loaded with `defer`, so it never blocks rendering.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Horizontal carousels -------------------------------------------------
   * The testimonial and 3-up bands are scroll-snap rows. They work with touch
   * and with a trackpad's horizontal gesture, but a plain mouse wheel only
   * scrolls vertically, so on a desktop with a mouse the row looked frozen -
   * which is exactly what "the carousel isn't working" means.
   *
   * This translates vertical wheel intent into horizontal scrolling while the
   * pointer is over a row, and only while that row still has somewhere to go.
   * Once it hits an end, the event is left alone so the page keeps scrolling
   * normally instead of trapping the wheel.
   */
  function enableWheelScroll(row) {
    row.addEventListener(
      'wheel',
      function (event) {
        // A real horizontal gesture (trackpad) already works; don't double-handle.
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

        var max = row.scrollWidth - row.clientWidth;
        if (max <= 0) return;

        var atStart = row.scrollLeft <= 0;
        var atEnd = row.scrollLeft >= max - 1;
        if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return;

        event.preventDefault();
        row.scrollBy({ left: event.deltaY, behavior: reduceMotion ? 'auto' : 'smooth' });
      },
      { passive: false }
    );
  }

  /* Marks a row as scrollable so CSS can show the fade affordance, and keeps
   * that in sync as it scrolls or the viewport changes. */
  function trackOverflow(row) {
    function update() {
      var max = row.scrollWidth - row.clientWidth;
      row.dataset.kzOverflow = max > 4 ? 'true' : 'false';
      row.dataset.kzAtEnd = row.scrollLeft >= max - 4 ? 'true' : 'false';
    }
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // Section ids are suffixed per template, so match on the suffix. Theme JSON
  // cannot add attributes to these rows, so the selector lives here.
  var CAROUSELS = [
    // The three-step band. On a phone it is already a swipe; this gives it the
    // same wheel-to-horizontal behaviour on desktop as the other rows, so a
    // mouse can reach the third step without the page jumping past the band.
    '[id$="__section_ritual"] .layout-panel-flex--row',
    '[id$="__testimonials"] .layout-panel-flex--row',
    '[id$="__section_ingredients"] .layout-panel-flex--row',
    '[id$="__section_Exd6UM"] .layout-panel-flex--row',
  ].join(',');

  /* --- Menu drawer: close on outside click and Escape -----------------------
   * The drawer is a native <details>. That gives it open/close for free, but
   * <details> has no concept of "outside", so tapping the page behind it did
   * nothing and the only way out was the toggle itself - which is easy to miss
   * once the panel covers most of the screen.
   *
   * Bound once at document level rather than per-drawer, so it keeps working
   * for markup added later.
   */
  function closeDrawers() {
    document.querySelectorAll('details.menu-drawer-container[open]').forEach(function (details) {
      // Delegate to the component rather than stripping [open] ourselves.
      //
      // The theme animates the close off a `menu-open` class and only removes
      // [open] once the transition finishes, and the toggle's open/closed icon
      // is driven by the same state. Removing [open] directly skipped both: the
      // panel vanished with no animation and the summary was left showing its
      // close (X) icon for a drawer that was no longer there.
      var component = details.closest('header-drawer');
      if (component && typeof component.close === 'function') {
        component.close();
        return;
      }

      // Fallback only if the component has not upgraded yet.
      details.removeAttribute('open');
      var summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
  }

  function bindDrawerDismiss() {
    if (document.documentElement.dataset.kzDrawerBound) return;
    document.documentElement.dataset.kzDrawerBound = '1';

    document.addEventListener(
      'pointerdown',
      function (event) {
        var open = document.querySelector('details.menu-drawer-container[open]');
        if (!open) return;
        // A click inside the panel, or on the toggle itself, is not "outside".
        if (event.target.closest('details.menu-drawer-container')) return;
        closeDrawers();
      },
      { capture: true }
    );

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeDrawers();
    });

    // Following a link should also dismiss it, so returning via the back button
    // does not land on a page with the menu still hanging open.
    document.addEventListener('click', function (event) {
      var link = event.target.closest('details.menu-drawer-container a[href]');
      if (link) closeDrawers();
    });
  }

  /* --- Product rows ---------------------------------------------------------
   * Tag any section that renders the offer/action slabs so the shared
   * product-row CSS applies to it.
   *
   * A Shopify section cannot be given an arbitrary class from theme JSON, and
   * the rules were originally keyed to one homepage section id - which meant
   * every other template rebuilt the same row and silently got none of the
   * behaviour. Marking by what a section *contains* rather than by which
   * section it is keeps the pattern portable: a page gets it by using the
   * slabs, without anyone having to remember to add a selector.
   */
  function tagProductRows() {
    var rows = document.querySelectorAll('.layout-panel-flex--row');
    Array.prototype.forEach.call(rows, function (row) {
      // Two or more offers, not one.
      //
      // The first version tagged any section containing a single
      // `.kz-slab--deal`, which is not the same thing as a row of products.
      // A band offering one deal on the set - "Save 20% on all three" - is a
      // column of copy beside a ledger, and tagging it applied the whole
      // product-row treatment to it: the step counter numbered the band's own
      // eyebrow, so it rendered "01 ALL THREE", and on mobile it became a
      // viewport-wide scroll-snap container that only looked right because a
      // `mobile-column` row never scrolls sideways anyway.
      //
      // What makes something a product row is several products side by side,
      // so that is what gets counted.
      if (row.querySelectorAll('.kz-slab--deal').length < 2) return;
      row.classList.add('kz-row');
      var section = row.closest('.shopify-section, .section');
      if (section) section.classList.add('kz-row');
    });
  }

  function init() {
    bindDrawerDismiss();
    tagProductRows();
    var rows = document.querySelectorAll(CAROUSELS);
    rows.forEach(function (row) {
      if (row.dataset.kzInit) return;
      row.dataset.kzInit = '1';
      enableWheelScroll(row);
      trackOverflow(row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-render in the theme editor without a page load.
  document.addEventListener('shopify:section:load', init);
})();
